import { useCallback } from 'react';
import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';
import loadable from '@loadable/component';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import { CustomCellEditor, CustomCellProps as Props } from '../types';

import { isCustomCellOfKind, drawCellError } from '../utils';

import { getValueAsString, validatePastedValue } from './utils';

import type { SharesCell } from './types';
import type { NumberFormatValues } from '../NumericInput';

import { kind } from './types';

const NumericInput = loadable(() => import('../NumericInput'), {
  resolveComponent: (c) => c.NumericInput,
});

const Editor: CustomCellEditor<SharesCell> = ({
  value: cell,
  onChange,
  onFinishedEditing,
}: Props<SharesCell>) => {
  const handleChange = useCallback(
    (value: NumberFormatValues) => {
      const { floatValue } = value;
      const validatedValue =
        typeof floatValue === 'undefined'
          ? null
          : Number.isNaN(floatValue ?? 0)
            ? 0
            : floatValue;
      onChange({
        ...cell,
        data: {
          ...cell.data,
          value: validatedValue,
          error: null,
        },
      });
    },
    [onChange, cell]
  );

  const handleBlur = useCallback(() => {
    onFinishedEditing(cell);
  }, [onFinishedEditing, cell]);

  return (
    <div className="grid items-center h-[53px] px-x0_75">
      <NumericInput
        aria-label="Shares"
        autoFocus={true}
        value={cell.data.value ?? undefined}
        onValueChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  );
};

export const Render: CustomRenderer<SharesCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, error } = cell.data;
    const isError = error !== null;

    if (isError) {
      drawCellError({ ctx, theme, rect, error });
    } else {
      const text = getValueAsString(value);
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        text,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    }

    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value } = cell.data;

    const text = getValueAsString(value);

    return (
      (value !== null ? ctx.measureText(text).width : 0) +
      theme.cellHorizontalPadding * 2
    );
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: validatePastedValue,
};
